<template>
  <div
    class="border p-2 flex flex-col gap-1"
    :class="backgroundColor"
  >
    <div class="flex justify-between items-center">
      <DiscountCategoryLabel
        :category="discount.category"
        :loyalty-level="discount.loyaltyLevel"
        :is-active="isActive"
      />

      <button
        v-if="canBeRemoved"
        class="flex items-center cursor-pointer"
        :aria-label="$t('Remove')"
        @click="$emit('clickDeleteCoupon', discount.id)"
      >
        <span class="iconify fa-pro-light--trash text-lg" />
      </button>
    </div>
    <div class="text-sm flex flex-col">
      <strong v-if="showExpondoPartnersEntryInfo">{{ thisOrderCountsLabel }}</strong>
      <span
        :class="{
          'font-bold': !showExpondoPartnersEntryInfo,
          'font-normal': showExpondoPartnersEntryInfo,
        }"
      >{{ discountTitle }}</span>
    </div>
    <CustomLink
      v-if="showExpondoPartnersEntryInfo"
      class="text-sm"
      :type="LINK_TYPE.Primary"
      :url="'/login'"
    >
      {{ loginRegisterLabel }}
    </CustomLink>
    <div
      v-if="showProductList"
      class="flex flex-col gap-1"
    >
      <DiscountCardItem
        v-for="(item, index) in discount.products"
        :key="index"
        :label="item.name"
        :amount="item.discountedAmount"
      />
    </div>
    <DiscountCardItem
      v-else
      :label="discountSubtitle"
      :amount="discount.discountedAmount"
    />
    <slot />
  </div>
</template>

<script lang="ts">

import type { MappedDiscount } from '~~/types/discounts';
import { DISCOUNT_CATEGORY } from '~~/types/discounts';
import { LINK_TYPE } from '~~/types/components/Link/LinkType';

export default defineComponent({
  name: 'DiscountCard',

  props: {
    discount: {
      type: Object as PropType<MappedDiscount>,
      required: true,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    // the Partners points in the default slot replace the spending target of an inactive loyalty discount
    hasPartnersPoints: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['clickDeleteCoupon'],
  setup(props) {

    const i18n = useI18n();

    const discountTitle = computed<string>(() => {

      switch (props.discount.category) {

        case DISCOUNT_CATEGORY.VOLUME:
          return i18n.t('discountPercentage-off-for-minimalQuantity', {
            discountPercentage: props.discount.value,
            minimalQuantity: props.discount.minimalQuantity,
          });
        case DISCOUNT_CATEGORY.DISTINCT_PRODUCTS:
          if (!props.discount.minimalQuantity) {

            return i18n.t('percentage-discount-unlocked', {
              discountPercentage: props.discount.value,
            });

          }

          return i18n.t('save-discountPercentage-for-minimalQuantity-different-products', {
            discountPercentage: props.discount.value,
            minimalQuantity: props.discount.minimalQuantity,
          });
        case DISCOUNT_CATEGORY.LOYALTY:
          if (props.isActive) {

            return i18n.t('percentage-discount-unlocked', {
              discountPercentage: props.discount.value,
            });

          } else if (props.discount.loyaltyLevel) {

            return i18n.t('personalized-discount.title.buy-more-for-your-discount');

          } else {

            return i18n.t('up-to-percentage-for-registered', {
              discount_percent: props.discount.value,
            });

          }
        default:
          return props.discount.name;

      }

    });

    const discountSubtitle = computed<string>(() => {

      switch (props.discount.category) {

        case DISCOUNT_CATEGORY.VOLUME:
          return '';
        case DISCOUNT_CATEGORY.DISTINCT_PRODUCTS:
          return props.isActive
            ? i18n.t('Discount applied')
            : i18n.t('Add more products to unlock instant savings.');
        case DISCOUNT_CATEGORY.LOYALTY:
          if (props.isActive) {

            return i18n.t('Your personal loyalty discount');

          } else if (props.hasPartnersPoints) {

            return '';

          } else if (props.discount.loyaltyLevel?.spendingRange?.from) {

            return i18n.t(
              'reach-amount-to-get-level-with-percentage',
              {
                amount: i18n.n(props.discount.loyaltyLevel.spendingRange.from, 'currency'),
                level: props.discount.loyaltyLevel.levelNumber,
                percentage: props.discount.value,
              },
            );

          } else {

            return '';

          }
        default:
          return i18n.t('Discount applied');

      }

    });

    const backgroundColor = computed(() => {

      if (props.isActive) {

        return 'bg-EXPONDO-green-extraLight border-EXPONDO-green ';

      } else {

        if (props.discount.category === DISCOUNT_CATEGORY.LOYALTY) {

          return 'bg-EXPONDO-secondary-light border-EXPONDO-secondary-medium';

        }

        return 'bg-EXPONDO-grey-5 border-EXPONDO-grey-20';

      }

    });

    const showProductList = computed(() => {

      return props.discount.category === DISCOUNT_CATEGORY.VOLUME &&
        props.discount.products.length > 0;

    });

    const canBeRemoved = computed(() => {

      return props.discount.category === DISCOUNT_CATEGORY.PROMO_CODE;

    });

    const showExpondoPartnersEntryInfo = computed(() => {

      return props.discount.category === DISCOUNT_CATEGORY.LOYALTY &&
        !props.isActive &&
        !props.discount.loyaltyLevel;

    });

    const thisOrderCountsLabel = computed(() => i18n.t('this-order-counts'));
    const loginRegisterLabel = computed(() => i18n.t('Login/Register'));

    return {
      LINK_TYPE,
      discountTitle,
      discountSubtitle,
      backgroundColor,
      showProductList,
      canBeRemoved,
      showExpondoPartnersEntryInfo,
      thisOrderCountsLabel,
      loginRegisterLabel,
    };

  },
});

</script>
